import type { KpiSeries, MeasurePolarity } from "../chartStudio/kpiTrend";
import type { NumberFormatSettings } from "../chartStudio/numberFormat";
/**
 * Widget model and WebSocket event contract for the "Generate Story" feature.
 *
 * StoryWidget is the view-model (camelCase, after mapping).
 * StoryWidgetApi is the raw shape that arrives over the WebSocket.
 * StoryGenEvent wraps every message the socket emits.
 */
import type { VisualizationSpec } from "vega-embed";
import type { ReportSourceRef } from "../kg/reportsApi.types";
import type { ChartDrillHierarchy } from "../storyCanvas/crossFilters.types";

// ─── Widget types ──────────────────────────────────────────────────────────────

export type StoryWidgetType =
  | "kpi"
  | "narrative"
  | "summary"
  | "chart"
  | "insight"
  // Embedded external report — same widget shape (ReportWidget) for all three;
  // the tag only discriminates how the item reached the canvas (search/browse,
  // already-synced connector asset, or the browser-extension capture plugin).
  | "report"
  | "ingested_widget"
  | "captured_widget"
  // Client-authored content items added from the canvas action bar (see
  // storyCanvasApi.ts createCanvasContentItem). Distinct from the
  // StoryWidget.types.ts "add-*" tags, which are the local, not-yet-saved
  // editing placeholders rendered before an item is persisted.
  | "link"
  | "embed"
  | "image"
  | "text";

/**
 * Render variant for the whole widget tree.
 *  - "default" → build/edit canvas: action items (AI edit, kebab) render.
 *  - "memo"    → memo/present mode: read-only styling per Figma, NO action items.
 */
export type StoryWidgetVariant = "default" | "memo";

/**
 * Backend-resolved grid position (revisit flow — see CANVAS_GET_API.md).
 * When present the canvas renders it verbatim; nothing is recomputed.
 */
export interface WidgetPosition {
  /** Columns from the left, on the canvas grid of `GRID_COLUMNS`. */
  x: number;
  /** Rows from the top (`ROW_HEIGHT` px rows). */
  y: number;
  /** Width in columns of the canvas grid of `GRID_COLUMNS`. */
  w: number;
  /** Height in rows. */
  h: number;
  /**
   * Columns of the grid the server holds this position on (12 for every
   * layout stored so far). Writes go back on that grid; see storyGridUnits.
   */
  storedColumns?: number;
  /**
   * The person fixed this tile's height (stored `h_fixed`). Absent / false:
   * a content-sized tile takes its measured content height over `h`.
   */
  heightFixed?: boolean;
}

/** A new-path text item's state for this reader: drives "Out of date · Rewrite". */
export interface StoryTextMark {
  state: "current" | "out_of_date" | "rewriting" | "not_written" | "withheld";
  edited: boolean;
  newerItems: number;
  note?: string;
}

interface BaseStoryWidget {
  id: string;
  type: StoryWidgetType;
  /**
   * Backend-resolved grid position. When present, Story Canvas renders it as
   * the source of truth; when absent, the frontend applies type-based fallback
   * defaults and persists the resolved geometry after meaningful placement.
   */
  position?: WidgetPosition;
  /**
   * Item-level lifecycle status from the canvas manifest.
   * `"accepted"` is the normal/confirmed state; any other value — including
   * explicit API `null` — triggers the accept/reject review overlay.
   * Absent (`undefined`) when the backend omits the field entirely.
   */
  status?: string | null;
}

/** Figma KPI card footer — source row (e.g. Qlik / Tableau + external link). */
export interface WidgetFooterSource {
  name: string;
  /** Logo URL; defaults from name when omitted (Tableau, Qlik). */
  logoSrc?: string;
  href?: string;
}

/** A chart tile Chart Studio is editing or creating (cs3 §2.4). */
export interface TileDraftState {
  status: "empty" | "drawing" | "needs" | "saving" | "error" | "saved";
  note: string | null;
  /** A chart being created: not an item yet (`draft:` id). */
  isTemporary?: boolean;
}
/** A chart or KPI tile whose item loaded with nothing to draw: a heading and why, in plain words. */
export interface TileUnavailable {
  title: string;
  reasons: string[];
}
/** Where a tile's story filters come from (subtitle "Filtered by …"). */
export interface TileFilteredBy {
  global: boolean;
  /** A cross-filter set on another chart. */
  otherChart: boolean;
  /** The chart's own filters, as chip labels. */
  own: string[];
  /** The rolling period the tile's effective date filter reads ("Last 30 days"); absent for none or fixed dates. */
  period?: string;
}

export interface KpiWidget extends BaseStoryWidget {
  type: "kpi";
  name: string;
  value: string;
  delta?: string;
  deltaPct?: string;
  trend?: "up" | "down" | "flat";
  /** e.g. "vs last month" */
  comparisonLabel?: string;
  /** Figma KPI card footer — source logo, name, and external link. */
  footerSource?: WidgetFooterSource;
  description?: { title: string; body: string };
  draft?: TileDraftState;
  filteredBy?: TileFilteredBy;
  /** PENDING: the KPI's recent series for the tile sparkline (oldest first); the API does not send it yet. */
  sparkline?: number[];
  /** The KPI's series over its date at a grain (a `kpi_trend` look), read from its spec; drives the area trend and % change. */
  trendSeries?: KpiSeries | null;
  /** Which way is good for the measure, when the model says; absent: up is good. */
  measurePolarity?: MeasurePolarity;
  /** The user's number format for the figures (the look's `number_format`); unset: the text as sent. */
  numberFormat?: NumberFormatSettings;
  unavailable?: TileUnavailable;
}

export interface NarrativeWidget extends BaseStoryWidget {
  type: "narrative";
  /** Optional section header */
  title?: string;
  body: string;
  /** AI gradient border treatment (e.g. the "AI summary"). Other narratives
   *  (executive summary, etc.) render a plain grey border. */
  accent?: boolean;
  textMark?: StoryTextMark;
}

/**
 * Standalone summary card. Renders through the same AiSummaryWidget design as
 * narrative/insight; per-kind dropdowns come from the sectionMenus config.
 * Same content shape as NarrativeWidget — a headline title + paragraph body.
 */
export interface SummaryWidget extends BaseStoryWidget {
  type: "summary";
  /** Optional section header (defaults to "AI summary" in the widget). */
  title?: string;
  body: string;
  /** AI gradient border treatment; plain grey border when false/omitted. */
  accent?: boolean;
  textMark?: StoryTextMark;
}

export interface ChartAiSummary {
  /** Bold headline stat for the primary summary (legacy single-section shape). */
  title: string;
  body: string;
  /**
   * Multi-section companion (summary + narrative + insights).
   * When present, all sections render in the chart summary panel top-to-bottom.
   */
  sections?: InsightSection[];
}

/**
 * Normalizes chart companion content — explicit `sections` win over title/body.
 * `headline` is only set for the legacy single-section shape (`title` → headline).
 */
export function resolveChartAiSummarySections(
  chartId: string,
  summary: ChartAiSummary,
): { sections: InsightSection[]; headline?: string } {
  if (summary.sections && summary.sections.length > 0) {
    return { sections: summary.sections, headline: summary.title };
  }
  return {
    sections: [
      {
        id: `${chartId}-summary`,
        kind: "summary",
        title: "AI summary",
        body: summary.body,
        collapsible: false,
      },
    ],
    headline: summary.title,
  };
}

/** Figma 3728:484541 "Follow up prompt" chip in the chart Link footer. */
export interface ChartFooterChip {
  id: string;
  label: string;
}

/** @deprecated Use WidgetFooterSource — shared with KPI footer. */
export type ChartFooterSource = WidgetFooterSource;

/** Figma 3728:484453 Modes option (Financial / Market share / …). */
export interface ChartModeOption {
  id: string;
  label: string;
}

export interface ChartWidget extends BaseStoryWidget {
  type: "chart";
  title: string;
  /** Vega-Lite declarative spec — returned by the AI, rendered as-is */
  spec: VisualizationSpec;
  /** The spec's language. Absent or `vega-lite`: Vega-Lite as always; `vega`: drawn with `<Vega>`. */
  specKind?: "vega-lite" | "vega";
  /** 1–2 sentence caption placed below the chart (narrative footer variant) */
  narrative?: string;
  /** When present, renders a split layout: chart left + AI summary right */
  aiSummary?: ChartAiSummary;
  /** New path: where the AI summary is (`rewriting` while it is being written), from the item's `ai_summary_state`. */
  aiSummaryState?: "rewriting" | "current" | "out_of_date" | "not_written";
  /**
   * Story-surface visibility for the AI summary companion.
   * From live `visibility.story.is_summary_visible` — null/missing → false.
   */
  isSummaryVisible?: boolean;
  /**
   * Story-surface visibility for the narrative companion section.
   * From live `visibility.story.is_narrative_visible` — null/missing → false.
   */
  isNarrativeVisible?: boolean;
  /**
   * Figma 3728:484538 Link footer chips (visible subset).
   * Remaining count drives "View N more" when `footerMoreCount` is set.
   */
  footerChips?: ChartFooterChip[];
  /** Extra chips not shown — Figma "View 2 more" uses this count. */
  footerMoreCount?: number;
  /** Right-side source (Tableau + external-link). */
  footerSource?: ChartFooterSource;
  /** Figma Modes segmented picker options. Hidden when absent/empty. */
  modes?: ChartModeOption[];
  /** Selected mode id (lifted). Required when `modes` is set. */
  activeModeId?: string;
  /**
   * Dimension hierarchies this chart can drill through (F4.1). Absent means
   * no drill down: the action renders disabled. Mocked until the story API
   * returns it.
   */
  drillHierarchies?: ChartDrillHierarchy[];
  draft?: TileDraftState;
  filteredBy?: TileFilteredBy;
  unavailable?: TileUnavailable;
}

export type InsightSectionKind =
  | "summary"
  | "narrative"
  | "recommendation"
  | "insights";

/**
 * Canonical display order when multiple sections share one AI card.
 * Per Figma insert rules, only `narrative` is nested into an existing summary
 * card; `recommendation` / `insights` spawn sibling cards instead.
 */
export const SECTION_KIND_ORDER: InsightSectionKind[] = [
  "summary",
  "narrative",
  "recommendation",
  "insights",
];

/** One section of the AI summary card (summary, narrative, recommendation, insights). */
export interface InsightSection {
  id: string;
  kind: InsightSectionKind;
  title: string;
  /** Paragraph sections (summary, narrative). */
  body?: string;
  /** Bullet-list sections (recommendation, insights). */
  bullets?: string[];
  collapsible?: boolean;
}

export interface InsightWidget extends BaseStoryWidget {
  type: "insight";
  /** Not yet sent by the backend for this type — reserved for when it is. */
  title?: string;
  /** 3–5 cross-story declarative statements */
  bullets: string[];
  /** Explicit sections win over the bullets-derived fallback. */
  sections?: InsightSection[];
  textMark?: StoryTextMark;
}

/**
 * Legacy fallback: an InsightWidget's flat `bullets` are all cross-story
 * declarative statements (no embedded summary) — render them as a single
 * bulleted "AI insights" section, not split into a synthetic summary.
 */
export const bulletsToSections = (widget: InsightWidget): InsightSection[] => {
  if (!widget.bullets || widget.bullets.length === 0) return [];
  return [
    {
      id: `${widget.id}-insights`,
      kind: "insights",
      title: "AI insights",
      bullets: widget.bullets,
      collapsible: true,
    },
  ];
};

/**
 * Embedded external report widget — backs "report" / "ingested_widget" /
 * "captured_widget" canvas items alike. Rendering only needs `connectionId` +
 * `embedUrl`: the same connection-id → JWT → <tableau-viz> pipeline the KG
 * report preview modal uses (see ReportEmbed molecule).
 */
/** A filter a BI tool applies to a report widget's view. */
export interface ReportToolFilter {
  index: number;
  field: string;
  values: (string | number | boolean | null)[];
  exclude?: boolean;
}

export interface ReportWidget extends BaseStoryWidget {
  type: "report" | "ingested_widget" | "captured_widget";
  title: string;
  source?: ReportSourceRef;
  connectorAssetId: string;
  /** Null until the connector connection is resolved — no embed can render yet. */
  connectionId: string | null;
  /** Null until the source report's embed URL is known. */
  embedUrl: string | null;
  thumbnailUrl?: string;
  kgEntityId?: string;
  /** Power BI visual id (SDK's `visualName`) — when set, only this visual is embedded instead of the whole report. Ignored for Tableau. */
  visualId?: string;
  /** Power BI visual display title — informational only (not used for embed matching). */
  visualTitle?: string;
  /** Power BI page name the visual lives on. Required alongside visualId for a single-visual embed; defaults to Power BI's default page name when absent. */
  pageName?: string;
  /** Power BI target: tile, visual, page or whole report (see ReportEmbed). */
  embedConfig?: {
    reportId?: string;
    groupId?: string;
    pageId?: string;
    visualId?: string;
    dashboardId?: string;
    tileId?: string;
  };
  /**
   * Salesforce only. Unlike Tableau/Power BI, Salesforce is captured LIVE —
   * there is no embedUrl for it (always null) — these three fields are what
   * the widget uses to call the live-refresh endpoint and fetch fresh
   * report/dashboard data on every render instead.
   */
  /** The view's link in its source tool, when known. */
  url?: string;
  /** Filters the tool applies to this view, from `tool_filters`. */
  toolFilters?: ReportToolFilter[];
  salesforceObjectType?: "report" | "dashboard";
  salesforceId?: string;
  instanceUrl?: string;
  /**
   * HubSpot only: a sanitized, static SVG snapshot captured client-side at
   * click time — rendered instead of an iframe embed (HubSpot has no
   * third-party embed-token API, so embedUrl is always null for it).
   */
  snapshotSvg?: string | null;
  /**
   * HubSpot only, optional: a user-defined object/metric/group-by/filters
   * query — when present, ReportCanvasWidget renders a native, live-
   * refreshing chart (via HubspotReportWidget) alongside/instead of the
   * static snapshotSvg above. undefined for a snapshot-only capture (no
   * query has been set yet — see hubspot_live_data_service.set_query).
   */
  hubspotQuery?: {
    object_type: string;
    metric: "count" | "sum" | "avg";
    metric_property?: string | null;
    group_by: string;
    filters: Array<{ property: string; operator: string; value: string }>;
    chart_type: "bar" | "line" | "pie";
  } | null;
  /**
   * Our own version of the view, drawn because a filter reached it that the view's tool can't
   * take: the chart's spec and why it shows. The embed stays a click away.
   */
  native?: ReportNativeChart;
}

export interface ReportNativeChart {
  spec: VisualizationSpec;
  specKind: "vega-lite" | "vega";
  /** Why our version shows: "Showing our own version of this chart, because Tableau's view can't filter by Category." */
  reasons: string[];
}

/** Persisted link content item — see LinkWidgetApi in storyCanvas.types.ts. */
export interface LinkContentWidget extends BaseStoryWidget {
  type: "link";
  /** The link's title as the Story keeps it; absent when it has none. */
  title?: string;
  content: string;
}

/** Persisted embed content item — content is a raw `<iframe>` HTML string. */
export interface EmbedContentWidget extends BaseStoryWidget {
  type: "embed";
  /** Not yet sent by the backend for this type — reserved for when it is. */
  title?: string;
  content: string;
}

/** Persisted image content item — see ImageWidgetApi in storyCanvas.types.ts. */
export interface ImageContentWidget extends BaseStoryWidget {
  type: "image";
  /** Not yet sent by the backend for this type — reserved for when it is. */
  title?: string;
  content: string;
  altText?: string;
}

/**
 * Rich-text styling for a persisted text content item — camelCase mirror of
 * TextFormattingApi (storyCanvas.types.ts). Duplicated here rather than
 * imported from components/molecules/TextWidget so the view-model layer
 * stays free of a components dependency.
 */
export interface TextContentFormatting {
  fontFamily?: string;
  fontSize?: number;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  alignment?: "left" | "center" | "right" | "justify";
  highlight?: boolean;
  highlightColor?: string;
}

/** Persisted text content item — see TextWidgetApi in storyCanvas.types.ts. */
export interface TextContentWidget extends BaseStoryWidget {
  type: "text";
  /** Not yet sent by the backend for this type — reserved for when it is. */
  title?: string;
  content: string;
  formatting?: TextContentFormatting;
  textMark?: StoryTextMark;
}

export type StoryWidget =
  | KpiWidget
  | NarrativeWidget
  | SummaryWidget
  | ChartWidget
  | InsightWidget
  | ReportWidget
  | LinkContentWidget
  | EmbedContentWidget
  | ImageContentWidget
  | TextContentWidget;

// ─── API (snake_case) widget shapes ───────────────────────────────────────────

export interface KpiWidgetApi {
  id: string;
  type: "kpi";
  name: string;
  value: string;
  delta?: string;
  delta_pct?: string;
  trend?: "up" | "down" | "flat";
  comparison_label?: string;
  footer_source?: { name: string; logo_src?: string; href?: string };
}

export interface NarrativeWidgetApi {
  id: string;
  type: "narrative";
  title?: string;
  body: string;
  accent?: boolean;
}

export interface SummaryWidgetApi {
  id: string;
  type: "summary";
  title?: string;
  body: string;
  accent?: boolean;
}

export interface ChartWidgetApi {
  id: string;
  type: "chart";
  title: string;
  spec: VisualizationSpec;
  /** @deprecated Prefer footer_chips / footer_source (Figma Link footer). */
  narrative?: string;
  ai_summary?: {
    title: string;
    body: string;
    sections?: InsightSectionApi[];
  };
  modes?: Array<{ id: string; label: string }>;
  active_mode_id?: string;
  footer_chips?: Array<{ id: string; label: string }>;
  footer_more_count?: number;
  footer_source?: { name: string; logo_src?: string; href?: string };
}

/** API section shape — same fields as the view-model `InsightSection`. */
export interface InsightSectionApi {
  id: string;
  kind: InsightSectionKind;
  title: string;
  body?: string;
  bullets?: string[];
  collapsible?: boolean;
}

export interface InsightWidgetApi {
  id: string;
  type: "insight";
  /** Legacy flat bullets — used when `sections` is omitted. */
  bullets?: string[];
  /** Explicit sections (summary / narrative / recommendation / insights). */
  sections?: InsightSectionApi[];
}

export type StoryWidgetApi =
  | KpiWidgetApi
  | NarrativeWidgetApi
  | SummaryWidgetApi
  | ChartWidgetApi
  | InsightWidgetApi;

// ─── WebSocket event contract ──────────────────────────────────────────────────

/**
 * One entry in the up-front generation plan — the backend announces every
 * widget it is about to produce (id + type) so the
 * canvas can render a skeleton in the correct grid slot before the data lands.
 */
export interface StoryGenPlanItem {
  id: string;
  type: StoryWidgetType;
  /**
   * Optional header label announced up-front so the skeleton shows the correct
   * title while loading (e.g. "AI summary" vs "Executive summary") instead of a
   * single hardcoded fallback.
   */
  title?: string;
  /** AI gradient border treatment for the loading slot (narrative only). */
  accent?: boolean;
}

/**
 * Generation plan — emitted once at the start of a run, before any widget data.
 * Drives the initial loading state (all slots show skeletons).
 */
export interface StoryGenPlanEvent {
  type: "plan";
  widgets: StoryGenPlanItem[];
}

/** One widget produced by the AI — revealed onto the canvas */
export interface StoryGenWidgetEvent {
  type: "widget";
  widget: StoryWidgetApi;
}

/** Generation pipeline status */
export interface StoryGenStatusEvent {
  type: "status";
  status: "thinking" | "generating" | "done";
}

/** Terminal error */
export interface StoryGenErrorEvent {
  type: "error";
  message: string;
}

export type StoryGenEvent =
  | StoryGenPlanEvent
  | StoryGenWidgetEvent
  | StoryGenStatusEvent
  | StoryGenErrorEvent;

// ─── Dynamic height ───────────────────────────────────────────────────────────

/**
 * Columns of the Story canvas grid, the unit every canvas x / w is in.
 *
 * 24 columns halve the 12-column cell the canvas used to snap to, so a tile
 * can be placed and sized in steps of about half the old width. The pixel
 * maths of react-grid-layout make the change exact: an x / w on 12 columns,
 * doubled, lands on the same pixels on 24 (same margin, same padding), so
 * every stored 12-column layout keeps its look. Stored positions are mapped
 * at the API boundary (`services/storyCanvas/storyGridUnits`).
 */
export const GRID_COLUMNS = 24;

/** Grid row unit size in pixels (react-grid-layout `rowHeight`). */
export const ROW_HEIGHT = 10;

/**
 * Vertical gap between rows in the stored layout's row math: a stored `h` spans
 * `h * ROW_HEIGHT + (h - 1) * GRID_MARGIN_Y` px, and a row starts every
 * `ROW_HEIGHT + GRID_MARGIN_Y` px. Never change it: every saved story's `h`
 * depends on it. The canvas draws a tighter visible gap by moving pixels from
 * the gap into its own rowHeight (see storyCanvasLayoutDefaults).
 */
export const GRID_MARGIN_Y = 12;

/**
 * Convert a measured content height (px) to grid row units.
 *
 * react-grid-layout renders an item of `h` units at a PIXEL height of
 * `h * ROW_HEIGHT + (h - 1) * GRID_MARGIN_Y` — i.e. it adds margin *between*
 * rows. Naively doing `ceil(px / ROW_HEIGHT)` ignores that added margin and
 * over-sizes every item ~2×, leaving large gaps below each widget. Inverting
 * the real formula keeps the item height ≈ the measured content height.
 */
export function pxToRowUnits(px: number): number {
  return Math.max(
    1,
    Math.ceil((px + GRID_MARGIN_Y) / (ROW_HEIGHT + GRID_MARGIN_Y)),
  );
}

/** Every widget molecule receives this so it can self-report its rendered height. */
export interface WidgetHeightProps {
  /** Reports the widget's measured content height, already converted to grid units. */
  onHeightChange?: (id: string, heightInUnits: number) => void;
}
